import React from 'react'

type UseFileDropType = {
  onDrop: (files: File[]) => void
}

const isEventWithFiles = event => {
  var temp = (event.originalEvent || event).dataTransfer
  return temp && (temp = temp.types) && temp.indexOf('Files') !== -1
}

const useFileDrop = ({ onDrop }: UseFileDropType) => {
  const [hovering, setHovering] = React.useState(false)
  const dropFuncRef = React.useRef(onDrop)

  // Keep function up to date but prevent re-renders
  React.useEffect(() => {
    dropFuncRef.current = onDrop
  }, [onDrop])

  const handleDrop = React.useCallback((ev: DragEvent) => {
    try {
      // Prevent file from opening in the browser
      ev.preventDefault()
      setHovering(false)

      const {
        dataTransfer: { items, files }
      } = ev

      if (!items) {
        dropFuncRef.current([...files])
        return
      }

      const droppedFiles = [...items]
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
      dropFuncRef.current(droppedFiles)
    } catch (error) {
      console.error('Error handling file drop', error)
    }
  }, [])

  // Triggered when a file is dragged into the window
  const handleDragOver = React.useCallback((ev: DragEvent) => {
    if (!isEventWithFiles(ev)) return

    // Prevent file from opening in the browser
    ev.preventDefault()
    if (!isEventWithFiles(ev)) return

    setHovering(true)
  }, [])

  // Triggered when a file stops being dragged over the window
  const handleDragLeave = React.useCallback((ev: DragEvent) => {
    setHovering(false)
  }, [])

  React.useEffect(() => {
    // Register file drag listeners
    document.addEventListener('drop', handleDrop)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('dragleave', handleDragLeave)

    return () => {
      // De-register file drag listeners
      document.removeEventListener('drop', handleDrop)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('dragleave', handleDragLeave)
    }
  }, [handleDrop, handleDragOver, handleDragLeave])

  return { hovering }
}

export default useFileDrop
